// SimulatorInputForm.js — Controlled form for investor simulation parameters.
// Mirrors the homepage yield calculator selector style.

import React from 'react';
import { useSimulator } from '../../context/SimulatorContext';
import { Play, RotateCcw } from 'lucide-react';

const MARKETS = [
  { value: 'Kenya', label: 'Kenya', enabled: true, ccy: 'KES' },
  { value: 'India', label: 'India', enabled: false, ccy: 'INR' },
  { value: 'Brazil', label: 'Brazil', enabled: false, ccy: 'BRL' },
  { value: 'Mexico', label: 'Mexico', enabled: false, ccy: 'MXN' },
  { value: 'South Africa', label: 'South Africa', enabled: false, ccy: 'ZAR' },
];

const SOURCE_CURRENCIES = ['USD', 'GBP', 'CHF', 'AED', 'JPY', 'EUR'];
const TENORS = [
  { value: 91, label: '91-day' },
  { value: 182, label: '182-day' },
  { value: 364, label: '364-day' },
];
const FORWARD_MODES = [
  { value: 'none', label: 'No hedge' },
  { value: 'market', label: 'Market forward (CIP)' },
  { value: 'custom', label: 'Custom premium' },
];
const ROLLOVER_MODES = [
  { value: 'off', label: 'No rollover (single cycle)' },
  { value: 'principal_only', label: 'Rollover principal only' },
  { value: 'principal_and_interest', label: 'Rollover principal + interest' },
];

export default function SimulatorInputForm() {
  const { params, setParams, runSimulation, loading, clearResults, mode } = useSimulator();

  const updateParam = (key, value) => {
    setParams(prev => ({ ...prev, [key]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    runSimulation();
  };

  const handleReset = () => {
    clearResults();
    setParams({
      sourceCurrency: 'USD',
      destinationCountry: 'Kenya',
      instrument: 'tbill',
      tenorDays: 91,
      investmentAmount: 100000,
      forwardMode: 'none',
      customPremiumBps: 0,
      rolloverMode: 'off',
      startDate: '2024-01-01',
      endDate: '2024-12-31',
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Investment Amount */}
      <div>
        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
          Investment Amount
        </label>
        <div className="flex gap-2">
          <select
            value={params.sourceCurrency}
            onChange={(e) => updateParam('sourceCurrency', e.target.value)}
            className="w-20 px-2 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20"
          >
            {SOURCE_CURRENCIES.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <input
            type="number"
            value={params.investmentAmount}
            onChange={(e) => updateParam('investmentAmount', parseFloat(e.target.value) || 0)}
            min="1000"
            step="1000"
            className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20"
          />
        </div>
      </div>

      {/* Destination Market */}
      <div>
        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
          Destination Market
        </label>
        <select
          value={params.destinationCountry}
          onChange={(e) => updateParam('destinationCountry', e.target.value)}
          className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20"
        >
          {MARKETS.map(m => (
            <option key={m.value} value={m.value} disabled={!m.enabled}>
              {m.label} ({m.ccy}){!m.enabled ? ' — Coming soon' : ''}
            </option>
          ))}
        </select>
      </div>

      {/* Tenor */}
      <div>
        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
          T-bill Tenor
        </label>
        <div className="flex gap-2">
          {TENORS.map(t => (
            <button
              key={t.value}
              type="button"
              onClick={() => updateParam('tenorDays', t.value)}
              className={`flex-1 py-2 text-sm rounded-lg border transition-colors ${
                params.tenorDays === t.value
                  ? 'bg-black text-white border-black'
                  : 'bg-gray-50 text-gray-700 border-gray-200 hover:border-gray-300'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Date Range */}
      {mode === 'historical' && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={params.startDate}
              onChange={(e) => updateParam('startDate', e.target.value)}
              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
              End Date
            </label>
            <input
              type="date"
              value={params.endDate}
              onChange={(e) => updateParam('endDate', e.target.value)}
              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20"
            />
          </div>
        </div>
      )}

      {/* Forward Protection */}
      <div>
        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
          FX Forward Protection
        </label>
        <div className="space-y-2">
          {FORWARD_MODES.map(fm => (
            <label key={fm.value} className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="forwardMode"
                value={fm.value}
                checked={params.forwardMode === fm.value}
                onChange={(e) => updateParam('forwardMode', e.target.value)}
                className="text-primary-600"
              />
              {fm.label}
            </label>
          ))}
          {params.forwardMode === 'custom' && (
            <input
              type="number"
              value={params.customPremiumBps}
              onChange={(e) => updateParam('customPremiumBps', parseFloat(e.target.value) || 0)}
              placeholder="Premium (bps)"
              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm mt-1"
            />
          )}
        </div>
      </div>

      {/* Rollover Mode */}
      <div>
        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
          Rollover
        </label>
        <select
          value={params.rolloverMode}
          onChange={(e) => updateParam('rolloverMode', e.target.value)}
          className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20"
        >
          {ROLLOVER_MODES.map(r => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </select>
      </div>

      {/* Actions */}
      <div className="flex gap-2 pt-2">
        <button
          type="submit"
          disabled={loading}
          className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-black text-white text-sm font-medium rounded-lg hover:bg-gray-800 disabled:bg-gray-400 transition-colors"
        >
          {loading ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <Play className="w-4 h-4" />
          )}
          {loading ? 'Running...' : 'Run Simulation'}
        </button>
        <button
          type="button"
          onClick={handleReset}
          className="px-3 py-2.5 bg-gray-100 text-gray-600 text-sm rounded-lg hover:bg-gray-200 transition-colors"
        >
          <RotateCcw className="w-4 h-4" />
        </button>
      </div>
    </form>
  );
}
